
import React, { useState, useContext, useEffect } from 'react'
import { SpinnerComponent } from 'react-element-spinner'
import { Button, Card, CardBody, CardFooter, CardHeader, Col, Container, Label, Row, Table } from 'reactstrap'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import PageTitleContextMenu from '../../../components/PageTitles/PageTitleContextMenu'
import VehiclePicSliderRedux from '../../../components/Slider/VehiclePicSliderRedux'
import Tires from '../../../components/VehicleInspection/Tires'
import Damages from '../../../components/VehicleInspection/Damages'
import Pictures from '../../../components/VehicleInspection/Pictures'
import PlaceMemberBidModal from '../../../components/Modals/PlaceMemberBidModal'
import { subscribe, unsubscribe } from '../../../events/customEvents'
import PlaceMemberBuyNowModal from '../../../components/Modals/PlaceMemberBuyNowModal'
import { AuctionsContext } from '../../../contexts/AuctionsProvider'
import VehiclesService from '../../../services/VehiclesService'
import SiteService from '../../../services/SiteService'
import { SessionContext } from '../../../contexts/SessionProvider'
import AuctionsService from '../../../services/AuctionsService'
import Enumeration from '../../../enumeration'
import OemsService from '../../../services/OemsService'
import VehicleInspectionsService from '../../../services/VehicleInspectionsService'
import CounterBidModal from '../../../components/Modals/CounterBidModal'

const MemberVehicleDetail = () =>
{
	const [isBusy, setIsBusy] = useState(false)
	const { t } = useTranslation()
	const navigate = useNavigate()
	const [showMemberBidModal, setShowMemberBidModal] = useState(false)
	const [showMemberBuyNowModal, setShowMemberBuyNowModal] = useState(false)
	const auctionsContext = useContext(AuctionsContext)
	const sessionContext = useContext(SessionContext)
	const [vehicleBuild, setVehicleBuild] = useState({ vehicleBuildId: null, vehicleId: null, modelYear: null, make: '', model: '', trim: '', modelCode: '', optionCodes: '', options: '' })
	const [carfaxReports, setCarfaxReports] = useState(undefined)
	const [componentViewSettings, setComponentViewSettings] = useState([])
	const [vehicleInspectionDamages, setVehicleInspectionDamages] = useState([])
	const [vehicleInspectionPictures, setVehicleInspectionPictures] = useState([])
	const [vehicleDisclosures, setVehicleDisclosures] = useState([])
	const [auctionTierMemberBid, setAuctionTierMemberBid] = useState(undefined)
	const [memberBidCounters, setMemberBidCounters] = useState([])
	const [showCounterBidModal, setShowCounterBidModal] = useState(false)
	const [auctionNotes, setAuctionNotes] = useState([])
	
	useEffect(() =>
	{
		retrieveComponentViewSettings('MemberVehicleDetail')
		retrieveBuildData()
		retrieveCarfaxReports()
		retrieveVehicleInspectionDamages()
		retrieveVehicleInspectionPictures()
		retrieveVehicleDisclosures()
		retrieveAuctionNotes()
		recordAuctionDetailImpression()
		
		subscribe('placeMemberBidModalClosed', handlePlaceMemberBidModalClosed)
		subscribe('placeMemberBuyNowModalClosed', handlePlaceMemberBuyNowModalClosed)
		subscribe('counterMemberBidModalClosed', handleCounterMemberBidModalClosed)
	}, [])
	
	useEffect(() =>
	{
		return () =>
		{
			unsubscribe('placeMemberBidModalClosed', handlePlaceMemberBidModalClosed)
			unsubscribe('placeMemberBuyNowModalClosed', handlePlaceMemberBuyNowModalClosed)
			unsubscribe('counterMemberBidModalClosed', handleCounterMemberBidModalClosed)
		}
	}, [])
	
	const recordAuctionDetailImpression = () =>
	{
		try
		{
			AuctionsService
				.addAuctionDetailImpression(auctionsContext.selectedAuctionListItem.auctionTier.auctionTierMemberId, sessionContext.loginSession.user.userSession.sessionKey)
				.then(result =>
				{
					sessionContext.updateSession(result.data.loginSession)
				})
				.catch((error) =>
				{
					console.log(error)
				})
		}
		catch {}
	}
	
	const handleCounterMemberBidModalClosed = (event) =>
	{
		setShowCounterBidModal(false)
	}
	
	const retrieveAuctionNotes = () =>
	{
		const publicOnly = true
		
		setIsBusy(true)
		AuctionsService
			.getAuctionNotes(auctionsContext.selectedAuctionListItem.auctionId, publicOnly, sessionContext.loginSession.user.userSession.sessionKey)
			.then(result =>
			{
				setIsBusy(false)
				sessionContext.updateSession(result.data.loginSession)
				
				setAuctionNotes(result.data.actionResult)
			})
			.catch((error) =>
			{
				setIsBusy(false)
				SiteService.displayApiError(error)
			})
	}
	
	const retrieveVehicleDisclosures = () =>
	{
		// Reset state before fetching so stale disclosures from a previously selected
		// vehicle never appear on the current VDP while the new fetch is in flight.
		setVehicleDisclosures([])
		setIsBusy(true)
		VehiclesService
			.getDisclosures(auctionsContext.selectedAuctionListItem.vehicleId, sessionContext.loginSession.user.userSession.sessionKey)
			.then(result =>
			{
				setIsBusy(false)
				sessionContext.updateSession(result.data.loginSession)
				
				setVehicleDisclosures(result.data.actionResult)
			})
			.catch((error) =>
			{
				setIsBusy(false)
				SiteService.displayApiError(error)
			})
	}
	
	const retrieveVehicleInspectionPictures = () =>
	{
		setIsBusy(true)
		VehicleInspectionsService
			.getVehicleInspectionPictures(auctionsContext.selectedAuctionListItem.auctionId, sessionContext.loginSession.user.userSession.sessionKey)
			.then(result =>
			{
				setIsBusy(false)
				sessionContext.updateSession(result.data.loginSession)
				
				setVehicleInspectionPictures(result.data.actionResult)
			})
			.catch((error) =>
			{
				setIsBusy(false)
				SiteService.displayApiError(error)
			})
	}
	
	const retrieveVehicleInspectionDamages = () =>
	{
		setIsBusy(true)
		VehicleInspectionsService
			.getVehicleInspectionDamages(auctionsContext.selectedAuctionListItem.auctionId, sessionContext.loginSession.user.userSession.sessionKey)
			.then(result =>
			{
				setIsBusy(false)
				sessionContext.updateSession(result.data.loginSession)
				
				setVehicleInspectionDamages(result.data.actionResult)
			})
			.catch((error) =>
			{
				setIsBusy(false)
				SiteService.displayApiError(error)
			})
	}
	
	const retrieveComponentViewSettings = (componentName) =>
	{
		setIsBusy(true)
		OemsService
			.getComponentViewSettings(componentName, sessionContext.loginSession.user.userSession.sessionKey)
			.then(result =>
			{
				setIsBusy(false)
				sessionContext.updateSession(result.data.loginSession)
				
				setComponentViewSettings(result.data.actionResult)
			})
			.catch((error) =>
			{
				setIsBusy(false)
				SiteService.displayApiError(error)
			})
	}
	
	const retrieveCarfaxReports = () =>
	{
		// Clear previous vehicle's Carfax links so we don't briefly render stale links
		// for the new vehicle while the API call is in flight.
		setCarfaxReports(undefined)
		setIsBusy(true)
		AuctionsService
			.getCarfaxReports(auctionsContext.selectedAuctionListItem.vehicleId, sessionContext.loginSession.user.userSession.sessionKey)
			.then(result =>
			{
				setIsBusy(false)
				sessionContext.updateSession(result.data.loginSession)
				
				if (result.data.actionResult)
				{
					setCarfaxReports(result.data.actionResult)
				}
			})
			.catch((error) =>
			{
				setIsBusy(false)
				SiteService.displayApiError(error)
			})
	}
	
	const retrieveBuildData = () =>
	{
		setIsBusy(true)
		VehiclesService
			.getBuildData(auctionsContext.selectedAuctionListItem.vehicleId, sessionContext.loginSession.user.userSession.sessionKey)
			.then(result =>
			{
				setIsBusy(false)
				sessionContext.updateSession(result.data.loginSession)
				
				if (result.data.actionResult)
				{
					setVehicleBuild(result.data.actionResult)
				}
			})
			.catch((error) =>
			{
				setIsBusy(false)
				SiteService.displayApiError(error)
			})
	}
	
	const handleChangePrimaryDisplayImage = (imageUrl) =>
	{
		auctionsContext.setSelectedAuctionListItem((previousSelectedAuctionListItem) =>
		{
			let currentSelectedAuctionListItem = {...previousSelectedAuctionListItem}
			currentSelectedAuctionListItem.primaryDisplayImageUrl = imageUrl
			return currentSelectedAuctionListItem
		})
	}
	
	const handleGoBack = () =>
	{
		navigate(auctionsContext.backRoute)
	}
	
	const handleBuyNow = () =>
	{
		setShowMemberBuyNowModal((previousShowMemberBuyNowModal) =>
		{
			return true
		})
	}
	
	const handlePlaceBid = () =>
	{
		auctionsContext.setSelectedAuctionListItem((previousSelectedAuctionListItem) =>
		{
			let currentSelectedAuctionListItem = { ...previousSelectedAuctionListItem }
			
			if (currentSelectedAuctionListItem.auctionTier.yourBid && currentSelectedAuctionListItem.auctionTier.yourBid.counterBid)
			{
				setupCounterBids(currentSelectedAuctionListItem)
			}
			else
			{
				setShowMemberBidModal(true)
			}
			
			return currentSelectedAuctionListItem
		})
	}
	
	const setupCounterBids = (auctionListItem) =>
	{
		setAuctionTierMemberBid((previousAuctionTierMemberBid) =>
		{
			return auctionListItem.auctionTier.yourBid
		})
		
		setIsBusy(true)
		AuctionsService
			.getMemberBidCounters(auctionListItem.auctionTier.yourBid.auctionTierMemberBidId, sessionContext.loginSession.user.userSession.sessionKey)
			.then(result =>
			{
				setIsBusy(false)
				sessionContext.updateSession(result.data.loginSession)
				
				setMemberBidCounters(result.data.actionResult)
				setShowCounterBidModal(true)
			})
			.catch((error) =>
			{
				setIsBusy(false)
				SiteService.displayApiError(error)
			})
	}
	
	const handlePlaceMemberBidModalClosed = (event) =>
	{
		setShowMemberBidModal(false)
	}
	
	const handlePlaceMemberBuyNowModalClosed = (event) =>
	{
		setShowMemberBuyNowModal(false)
	}
	
	const viewCarfaxReport = (reportLink) =>
	{
		window.open(reportLink, 'Carfax Report', "resizable=yes,toolbar=no,scrollbars=yes,menubar=no,status=no,directories=no")
	}
	
	return (
		<>
			<SpinnerComponent
				loading={ isBusy }
				position="global"
				color="#3578e5"
				secondaryColor="#f3f3f3"
				backgroundColor="#d3d3d3"
				spinnerType="circle-dots"
			/>
			<PageTitleContextMenu
				heading={ t('Vehicles.Detail.Title') }
				subheading=""
				icon="pe-7s-car icon-gradient bg-premium-dark"
				headerOptions={
					{
						showBack: (auctionsContext.backRoute !== ''),
						backCommand: (auctionsContext.backRoute === '') ? null : () => { handleGoBack() },
						showReload: false,
						reloadCommand: null,
						showResort: false,
						resortCommand: null,
						showFilter: false,
						filterCommand: null
					}
				}
			/>
			<Container className="mt--6" fluid>
				<Row>
					<Col md="6">
						<Card>
							<CardBody>
								<Row>
									<Col md="12">
										<div className="text-center" style={{ marginBottom: '10px' }}>
											<img
												className="inspection-pic-sm"
												src={ auctionsContext.selectedAuctionListItem.primaryDisplayImageUrl }
												alt="..."
												width="538"
												height="410"
											/>
										</div>
									</Col>
								</Row>
								<Row>
									<Col md="12">
										<VehiclePicSliderRedux
											vehiclePics={ auctionsContext.selectedAuctionListItem.vehicleInspectionPictures }
											changePrimaryDisplayImage={ handleChangePrimaryDisplayImage }
											style={{ marginTop: '10px' }}
										/>
									</Col>
								</Row>
							</CardBody>
						</Card>
					</Col>
					<Col md="6">
						<Card>
							<CardHeader>
								<Label>
									{ auctionsContext.selectedAuctionListItem.year + ' ' + auctionsContext.selectedAuctionListItem.make + ' ' + auctionsContext.selectedAuctionListItem.model }
								</Label>
							</CardHeader>
							<CardBody>
								<Row>
									<Col md={6} style={{ fontSize: 'smaller' }}>
										<Row className="py-1 align-items-center">
											<Col sm="5">
												<div className="text-xs text-uppercase text-muted font-weight-bold">{ (auctionsContext.selectedAuctionListItem && auctionsContext.selectedAuctionListItem.auctionTier) ? `Tier: ${auctionsContext.selectedAuctionListItem.auctionTier.auctionTierName}` : '' }</div>
											</Col>
											<Col sm="7">
												<Label>{ (auctionsContext.selectedAuctionListItem && auctionsContext.selectedAuctionListItem.auctionTier) ? auctionsContext.selectedAuctionListItem.timeDisplay.h + 'h ' + auctionsContext.selectedAuctionListItem.timeDisplay.m + 'm ' + auctionsContext.selectedAuctionListItem.timeDisplay.s + 's' : '' }</Label>
											</Col>
										</Row>
										<Row className="py-1 align-items-center">
											<Col sm="5">
												<div className="text-xs text-uppercase text-muted font-weight-bold">{ t('Vehicles.Detail.Auction_End_Time') }</div>
											</Col>
											<Col sm="7">
												<Label>{ (auctionsContext.selectedAuctionListItem && auctionsContext.selectedAuctionListItem.auctionTier) ? SiteService.dateTimeDisplay(auctionsContext.selectedAuctionListItem.auctionTier.tierUtcEndTime) : '' }</Label>
											</Col>
										</Row>
										{
											(auctionsContext.selectedAuctionListItem && auctionsContext.selectedAuctionListItem.auctionTier.auctionTierTypeId !== Enumeration.AuctionTierTypes.BiddingTier) &&
											<Row className="py-1 align-items-center">
												<Col sm="5">
													<div className="text-xs text-uppercase text-muted font-weight-bold">{t('Vehicles.Detail.Market_Price')}</div>
												</Col>
												<Col sm="7">
													<Label>{ (auctionsContext.selectedAuctionListItem.marketPrice) ? auctionsContext.selectedAuctionListItem.marketPrice.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : 'N/A' }</Label>
												</Col>
											</Row>
										}
										{
											(auctionsContext.selectedAuctionListItem && auctionsContext.selectedAuctionListItem.auctionTier.auctionTierTypeId === Enumeration.AuctionTierTypes.BiddingTier && auctionsContext.selectedAuctionListItem.startPrice > auctionsContext.selectedAuctionListItem.highestBidAmount) &&
											<Row className="py-1 align-items-center">
												<Col sm="5">
													<div className="text-xs text-uppercase text-muted font-weight-bold">{t('Vehicles.Detail.Start_Price')}</div>
												</Col>
												<Col sm="7">
													<Label>{ (auctionsContext.selectedAuctionListItem.startPrice) ? auctionsContext.selectedAuctionListItem.startPrice.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : 'N/A' }</Label>
												</Col>
											</Row>
										}
										{
											(auctionsContext.selectedAuctionListItem && auctionsContext.selectedAuctionListItem.auctionTier.auctionTierTypeId === Enumeration.AuctionTierTypes.BiddingTier) &&
											<Row className="py-1 align-items-center">
												<Col sm="5">
													<div className="text-xs text-uppercase text-muted font-weight-bold">{t('Vehicles.Detail.Highest_Bid')}</div>
												</Col>
												<Col sm="7">
													<Label>{ (auctionsContext.selectedAuctionListItem && auctionsContext.selectedAuctionListItem.auctionTier.auctionTierTypeId === Enumeration.AuctionTierTypes.BiddingTier && auctionsContext.selectedAuctionListItem.highestBidAmount) ? auctionsContext.selectedAuctionListItem.highestBidAmount.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : 'N/A' }</Label>
												</Col>
											</Row>
										}
										{
											(auctionsContext.selectedAuctionListItem && auctionsContext.selectedAuctionListItem.auctionTier.auctionTierTypeId === Enumeration.AuctionTierTypes.BiddingTier) &&
											<Row className="py-1 align-items-center">
												<Col sm="5">
													<div className="text-xs text-uppercase text-muted font-weight-bold">{t('Vehicles.Detail.Your_Bid')}</div>
												</Col>
												<Col sm="7">
													<Label>{ (auctionsContext.selectedAuctionListItem && auctionsContext.selectedAuctionListItem.auctionTier.auctionTierTypeId === Enumeration.AuctionTierTypes.BiddingTier && auctionsContext.selectedAuctionListItem.auctionTier && auctionsContext.selectedAuctionListItem.auctionTier.yourBid) ? auctionsContext.selectedAuctionListItem.auctionTier.yourBid.bidAmount.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : 'N/A' }</Label>
												</Col>
											</Row>
										}
										{
											(SiteService.isElementViewable('MemberVehicleDetail','BuyNow_Price', componentViewSettings) && auctionsContext.selectedAuctionListItem.auctionTier.auctionTierTypeId === Enumeration.AuctionTierTypes.BiddingTier && (auctionsContext.selectedAuctionListItem.buyNowPrice && auctionsContext.selectedAuctionListItem.buyNowPrice > 0)) &&
											<Row className="py-1 align-items-center">
												<Col sm="5">
													<div className="text-xs text-uppercase text-muted font-weight-bold">{t('Vehicles.Detail.Buy_Now_Price')}</div>
												</Col>
												<Col sm="7">
													<Label>{ (auctionsContext.selectedAuctionListItem && auctionsContext.selectedAuctionListItem.buyNowPrice) ? auctionsContext.selectedAuctionListItem.buyNowPrice.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : 'N/A' }</Label>
												</Col>
											</Row>
										}
										<Row><Col sm="12"><hr className="my-2"/></Col></Row>
										<Row className="py-1 align-items-center">
											<Col sm="5">
												<div className="text-xs text-uppercase text-muted font-weight-bold">{ t('Vehicles.Detail.Model_Year') }</div>
											</Col>
											<Col sm="7">
												<Label>{ vehicleBuild.modelYear || auctionsContext.selectedAuctionListItem.year || 'N/A' }</Label>
											</Col>
										</Row>
										<Row className="py-1 align-items-center">
											<Col sm="5">
												<div className="text-xs text-uppercase text-muted font-weight-bold">{ t('Vehicles.Detail.Make') }</div>
											</Col>
											<Col sm="7">
												<Label>{ vehicleBuild.make || auctionsContext.selectedAuctionListItem.make || 'N/A' }</Label>
											</Col>
										</Row>
										<Row className="py-1 align-items-center">
											<Col sm="5">
												<div className="text-xs text-uppercase text-muted font-weight-bold">{ t('Vehicles.Detail.Model') }</div>
											</Col>
											<Col sm="7">
												<Label>{ vehicleBuild.model || auctionsContext.selectedAuctionListItem.model || 'N/A' }</Label>
											</Col>
										</Row>
										<Row className="py-1 align-items-center">
											<Col sm="5">
												<div className="text-xs text-uppercase text-muted font-weight-bold">{ t('Vehicles.Detail.Trim') }</div>
											</Col>
											<Col sm="7">
												<Label>{ vehicleBuild.trim || auctionsContext.selectedAuctionListItem.trim || 'N/A' }</Label>
											</Col>
										</Row>
										<Row className="py-1 align-items-center">
											<Col sm="5">
												<div className="text-xs text-uppercase text-muted font-weight-bold">{ t('Vehicles.Detail.Model_Code') }</div>
											</Col>
											<Col sm="7">
												<Label>{ vehicleBuild.modelCode }</Label>
											</Col>
										</Row>
										<Row className="py-1 align-items-center">
											<Col sm="5">
												<div className="text-xs text-uppercase text-muted font-weight-bold">{ t('Vehicles.Detail.Option_Codes') }</div>
											</Col>
											<Col sm="7">
												<Label>{ vehicleBuild.optionCodes }</Label>
											</Col>
										</Row>
										<Row className="py-1 align-items-center">
											<Col sm="5">
												<div className="text-xs text-uppercase text-muted font-weight-bold">{ t('Vehicles.Detail.Options') }</div>
											</Col>
											<Col sm="7">
												<Label>{ vehicleBuild.options }</Label>
											</Col>
										</Row>
									</Col>
									<Col md={6} style={{ fontSize: 'smaller' }}>
										<Row className="py-1 align-items-center">
											<Col sm="5">
												<div className="text-xs text-uppercase text-muted font-weight-bold">{ t('Vehicles.Detail.Vin') }</div>
											</Col>
											<Col sm="7">
												<Label>{ auctionsContext.selectedAuctionListItem.serialNumber }</Label>
											</Col>
										</Row>
										<Row className="py-1 align-items-center">
											<Col sm="5">
												<div className="text-xs text-uppercase text-muted font-weight-bold">{ t('Vehicles.Detail.Mileage') }</div>
											</Col>
											<Col sm="7">
												<Label>{ auctionsContext.selectedAuctionListItem.mileage ? auctionsContext.selectedAuctionListItem.mileage.toLocaleString() : 'N/A' }</Label>
											</Col>
										</Row>
										<Row><Col sm="12"><hr className="my-2"/></Col></Row>
										<Row className="py-1 align-items-center">
											<Col sm="5">
												<div className="text-xs text-uppercase text-muted font-weight-bold">{ t('Vehicles.Detail.Exterior_Color') }</div>
											</Col>
											<Col sm="7">
													<Label>{ auctionsContext.selectedAuctionListItem.exteriorColor || 'N/A' }</Label>
												</Col>
											</Row>
											<Row className="py-1 align-items-center">
												<Col sm="5">
													<div className="text-xs text-uppercase text-muted font-weight-bold">{ t('Vehicles.Detail.Interior_Color') }</div>
												</Col>
												<Col sm="7">
													<Label>{ auctionsContext.selectedAuctionListItem.interiorColor || 'N/A' }</Label>
											</Col>
										</Row>
										<Row><Col sm="12"><hr className="my-2"/></Col></Row>
										<Row className="py-1 align-items-center">
											<Col sm="5">
												<div className="text-xs text-uppercase text-muted font-weight-bold">{ t('Vehicles.Detail.Engine') }</div>
											</Col>
											<Col sm="7">
													<Label>{ auctionsContext.selectedAuctionListItem.engine || 'N/A' }</Label>
											</Col>
										</Row>
										<Row className="py-1 align-items-center">
											<Col sm="5">
												<div className="text-xs text-uppercase text-muted font-weight-bold">{ t('Vehicles.Detail.Fuel_Type') }</div>
											</Col>
											<Col sm="7">
													<Label>{ auctionsContext.selectedAuctionListItem.fuelType || 'N/A' }</Label>
											</Col>
										</Row>
										<Row className="py-1 align-items-center">
											<Col sm="5">
												<div className="text-xs text-uppercase text-muted font-weight-bold">{ t('Vehicles.Detail.Drivetrain') }</div>
											</Col>
											<Col sm="7">
													<Label>{ auctionsContext.selectedAuctionListItem.drivetrain || 'N/A' }</Label>
											</Col>
										</Row>
										<Row className="py-1 align-items-center">
											<Col sm="5">
												<div className="text-xs text-uppercase text-muted font-weight-bold">{ t('Vehicles.Detail.Transmission') }</div>
											</Col>
											<Col sm="7">
													<Label>{ auctionsContext.selectedAuctionListItem.transmission || 'N/A' }</Label>
											</Col>
										</Row>
										<Row><Col sm="12"><hr className="my-2"/></Col></Row>
										<Row className="py-1 align-items-center">
											<Col sm="5">
												<div className="text-xs text-uppercase text-muted font-weight-bold">{ t('Vehicles.Detail.Location') }</div>
											</Col>
											<Col sm="7">
												{ (auctionsContext.selectedAuctionListItem.locationCity) &&
													<Label>{  auctionsContext.selectedAuctionListItem.locationCity + ', ' + auctionsContext.selectedAuctionListItem.locationProvince }</Label>
												}
											</Col>
										</Row>
										<Row className="py-1 align-items-center">
											<Col sm="5">
												<div className="text-xs text-uppercase text-muted font-weight-bold">{ t('Vehicles.Detail.Distance') }</div>
											</Col>
											<Col sm="7">
												<Label>{ auctionsContext.selectedAuctionListItem.proximity }</Label>
											</Col>
										</Row>
									</Col>
								</Row>
								<Row><Col sm="12"><hr className="my-2"/></Col></Row>
								<Row>
									<Col sm="6">
										<div className="text-xs text-uppercase text-muted font-weight-bold">
											{
												(carfaxReports && carfaxReports.carfaxEnglishReportLink)
													? <a href='#' onClick={ () => viewCarfaxReport(carfaxReports.carfaxEnglishReportLink) }>Carfax English Report</a>
													: <span className="text-muted">Carfax English Report not yet available</span>
											}
										</div>
									</Col>
									<Col sm="6">
										<div className="text-xs text-uppercase text-muted font-weight-bold">
											{
												(carfaxReports && carfaxReports.carfaxFrenchReportLink)
													? <a href='#' onClick={ () => viewCarfaxReport(carfaxReports.carfaxFrenchReportLink) }>Carfax French Report</a>
													: <span className="text-muted">Carfax French Report not yet available</span>
											}
										</div>
									</Col>
								</Row>
							</CardBody>
							<CardFooter>
								<Row style={{ marginTop: '5px', width: '100%' }}>
									<Col md="6">
										{
											SiteService.isDealerBuyNowVisible(auctionsContext.selectedAuctionListItem) &&
											<Button
												block
												color="secondary"
												className="btn-block"
												onClick={ () => handleBuyNow() }
												disabled={ ((!SiteService.isInRole(sessionContext.loginSession, Enumeration.UserRoles.AuctionBuyer) && !SiteService.isInRole(sessionContext.loginSession, Enumeration.UserRoles.AuctionHybrid)) || (auctionsContext.selectedAuctionListItem.secondsRemaining < 1)) }
											>
												{ t('Vehicles.Detail.Buy_Now') }
											</Button>
										}
									</Col>
									<Col md="6">
										{
											SiteService.isDealerPlaceBidVisible(auctionsContext.selectedAuctionListItem) &&
											<Button
												block
												color="primary"
												className="btn-block"
												onClick={ () => handlePlaceBid() }
												disabled={ ((!SiteService.isInRole(sessionContext.loginSession, Enumeration.UserRoles.AuctionBuyer) && !SiteService.isInRole(sessionContext.loginSession, Enumeration.UserRoles.AuctionHybrid)) || ((auctionsContext.selectedAuctionListItem.secondsRemaining < 1) && !(auctionsContext.selectedAuctionListItem.auctionTier && auctionsContext.selectedAuctionListItem.auctionTier.yourBid && auctionsContext.selectedAuctionListItem.auctionTier.yourBid.counterBid))) }
											>
												{
													SiteService.bidButtonLabel(auctionsContext.selectedAuctionListItem, t)
												}
											</Button>
										}
									</Col>
								</Row>
							</CardFooter>
						</Card>
					</Col>
				</Row>
				<Row>
					<Col md="12">
						<Card style={{ marginTop: '10px' }}>
							<CardHeader>
								<Label>{ t('Vehicles.Detail.Tires_Header') }</Label>
							</CardHeader>
							<CardBody>
								<Tires
									vehicleId={ auctionsContext.selectedAuctionListItem.vehicleId }
									isUserInAdminGroup={ SiteService.isInGroup(sessionContext.loginSession, Enumeration.UserGroups.Administrators) }
								/>
							</CardBody>
						</Card>
					</Col>
				</Row>
				<Row>
					<Col md="12">
						<Card style={{ marginTop: '10px' }}>
							<CardHeader>
								<Label>{ t('Vehicles.Detail.Damages_Header') }</Label>
							</CardHeader>
							<CardBody>
								<Damages vehicleInspectionDamages={ vehicleInspectionDamages } />
							</CardBody>
						</Card>
					</Col>
				</Row>
				<Row>
					<Col md="12">
						<Card style={{ marginTop: '10px' }}>
							<CardHeader>
								<Label>{ t('Vehicles.Detail.Pictures_Header') }</Label>
							</CardHeader>
							<CardBody>
								<Pictures vehicleInspectionPictures={ vehicleInspectionPictures } />
							</CardBody>
						</Card>
					</Col>
				</Row>
				<Row>
					<Col md="12">
						<Card style={{ marginTop: '10px' }}>
							<CardHeader>
								<Label>{ t('Vehicles.Detail.Disclosures_Header') }</Label>
							</CardHeader>
							<CardBody>
								<Row>
									<Col md="12">
										<div className="table-responsive">
											<Table className="align-items-center table-flush">
												<thead className="thead-light">
													<tr>
														<th className="sort" scope="col" colSpan={2}>{t('Auctions.Disclosures.Title')}</th>
													</tr>
												</thead>
													<tbody className="list">
													{vehicleDisclosures.map((disclosure, idx) => (
														<tr key={ idx } style={{cursor: 'pointer'}}>
															<td>{ disclosure.name }</td>
															<td>{ disclosure.value }</td>
														</tr>
													))}
													</tbody>
											</Table>
										</div>
										{
											vehicleDisclosures.length === 0 &&
											<Row style={{ marginTop: '20px' }}>
												<Col md="12" style={{ textAlign: 'center' }}>
													<Label>{ t('Auctions.Disclosures.No_Results') }</Label>
												</Col>
											</Row>
										}
									</Col>
								</Row>
								
								<Row>
									<Col md="12">
										<div className="table-responsive">
											<Table className="align-items-center table-flush">
												<thead className="thead-light">
												<tr>
													<th className="sort" scope="col" colSpan={2}>{t('Auctions.Notes.Title')}</th>
												</tr>
												</thead>
												<tbody className="list">
												{auctionNotes.map((auctionNote, idx) => (
													<tr key={ idx } style={{cursor: 'pointer'}}>
														<td>{ auctionNote.note }</td>
													</tr>
												))}
												</tbody>
											</Table>
										</div>
										{
											auctionNotes.length === 0 &&
											<Row style={{ marginTop: '20px' }}>
												<Col md="12" style={{ textAlign: 'center' }}>
													<label>{ t('Auctions.Notes.No_Results') }</label>
												</Col>
											</Row>
										}
									</Col>
								</Row>
							</CardBody>
						</Card>
					</Col>
				</Row>
				<Row><Col md="12"><p>&nbsp;</p></Col></Row>
			</Container>
			
			<PlaceMemberBidModal
				showMemberBidModal={ showMemberBidModal }
				auctionListItem={ auctionsContext.selectedAuctionListItem }
			/>
			
			<PlaceMemberBuyNowModal
				showMemberBuyNowModal={ showMemberBuyNowModal }
				auctionListItem={ auctionsContext.selectedAuctionListItem }
			/>
			
			<CounterBidModal
				showCounterBidModal={ showCounterBidModal }
				auctionTierMemberBid={ auctionTierMemberBid }
				memberBidCounters={ memberBidCounters }
			/>
		</>
	)
}

export default MemberVehicleDetail
